import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Machine, QueryParams } from '../types';
import {
  getMachine,
  getMachineById,
  getConversationLogs,
} from '../utils/httpRequest';
import { RootState } from '../store';
import { groupBy } from 'lodash';
import { ConversationLog } from '../types/events';

export interface MachineState {
  machines: Machine[];
  machine?: Machine;
  loading: boolean;
  error?: string;
  logs: ConversationLog[];
  logsLoading: boolean;
  logsError?: string;
}

const initialState: MachineState = {
  machines: [],
  loading: false,
  logs: [],
  logsLoading: false,
};

export const fetchMachines = createAsyncThunk(
  'machines/fetchMachines',
  async (params?: QueryParams) => {
    const machines = await getMachine(params);

    return machines;
  }
);

export const fetchMachineById = createAsyncThunk(
  'machines/fetchMachineById',
  async (id: string) => {
    const machine = await getMachineById(id);

    return machine;
  }
);

export const fetchConversationLogs = createAsyncThunk(
  'machines/fetchConversationLogs',
  async (type: 'diagnosis' | 'routine' | 'step' | '*') => {
    const res = await getConversationLogs(type);

    return res?.data || [];
  }
);

export const machineSlice = createSlice({
  name: 'machine',
  initialState,
  reducers: {
    setMachine: (state, action: PayloadAction<Machine>) => {
      state.machine = action.payload;
    },
    setMachines: (state, action: PayloadAction<Machine[]>) => {
      state.machines = action.payload;
    },
    addMachine: (state, action: PayloadAction<Machine>) => {
      state.machines = [action.payload, ...state.machines];
    },
    removeMachine: (state, action: PayloadAction<string>) => {
      state.machines = state.machines.filter(
        ({ machineID }) => machineID !== action.payload
      );
    },
    updateMachine: (state, action: PayloadAction<Machine>) => {
      const index = state.machines.findIndex(
        ({ machineID }) => machineID === action.payload.machineID
      );
      const nextMachines = [...state.machines];
      nextMachines[index] = action.payload;
      state.machines = [...nextMachines];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMachines.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchMachines.fulfilled, (state, action) => {
        state.machines = action.payload.data;
        state.loading = false;
        state.error = '';
      })
      .addCase(fetchMachines.rejected, (state, action) => {
        state.error = 'Get Machine List Failed.';
        state.loading = false;
      })
      .addCase(fetchMachineById.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchMachineById.fulfilled, (state, action) => {
        state.machine = action.payload.data;
        state.loading = false;
        state.error = '';
      })
      .addCase(fetchMachineById.rejected, (state, action) => {
        state.error = 'Get Machine Failed.';
        state.loading = false;
      })
      .addCase(fetchConversationLogs.pending, (state) => {
        state.logsLoading = true;
        state.logsError = '';
      })
      .addCase(fetchConversationLogs.fulfilled, (state, action) => {
        state.logs = action.payload;
        state.logsLoading = false;
        state.logsError = '';
      })
      .addCase(fetchConversationLogs.rejected, (state, action) => {
        state.logsError = 'Get Conversation Logs Failed.';
        state.logsLoading = false;
      });
  },
});

export const {
  setMachine,
  setMachines,
  addMachine,
  removeMachine,
  updateMachine,
} = machineSlice.actions;

export default machineSlice.reducer;

export const selectMachines = (state: RootState) => state.machines.machines;
export const selectMachine = (state: RootState) => state.machines.machine;

export const selectIsFetchingMachine = (state: RootState) =>
  state.machines.loading;

export const selectGroupedMachines = (state: RootState) => {
  const { machines } = state.machines;
  return groupBy(machines, 'type');
};

export const selectConversationLogs = (state: RootState) => state.machines.logs;
export const selectConversationLogsLoading = (state: RootState) =>
  state.machines.logsLoading;
export const selectConversationLogsError = (state: RootState) =>
  state.machines.logsError;
